import axios from "axios";

// Base API configuration
const API_BASE_URL =
  process.env.REACT_APP_BACKEND_URL || "https://api.citycourier.org/api/v1";

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor to handle common errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  },
);

export interface ConsignmentFilters {
  query?: string;
  status?: number | "";
  booking_type?: "COD" | "RETAIL" | "";
  customer_id?: number | "";
  destination?: number | "";
  from?: string;
  to?: string;
  page?: number;
  per_page?: number;
  // Node filters
  first_mile_node_id?: number | "";
  current_node_id?: number | "";
  last_mile_node_id?: number | "";
  // DateTime filters
  created_from?: string;
  created_to?: string;
  updated_from?: string;
  updated_to?: string;
}

export interface Consignee {
  name: string;
  phone: string;
  phone2?: string;
  address: string;
}

export interface Consigner {
  name: string;
  phone: string;
  phone2?: string;
  address: string;
  cnic?: string;
}

export interface City {
  id: number;
  name: string;
  code?: string;
}

export interface Status {
  id: number;
  name: string;
  badge?: string;
}

export interface User {
  id: number;
  name: string;
  clientProfile?: {
    company_name?: string;
    area_zone?: number;
    service_type?: string;
    authorized_phone?: string;
    address?: string;
  };
}

export interface Branch {
  id: number;
  name: string;
}

export interface Pricing {
  id: number;
  price?: number;
  discount?: number;
  total_price?: number;
}

export interface Tracking {
  id: number;
  status: Status;
  description: string;
  created_at: string;
}

export interface Scan {
  id: number;
  user: User;
  branch: Branch;
  city: City;
  created_at: string;
}

export interface Consignment {
  id: number;
  consignment_no: string;
  created_at: string;
  updated_at?: string;
  user?: User;
  consignee: Consignee;
  consigner?: Consigner;
  service_type: string;
  booking_type: string;
  cod_amount: number;
  cod_type?: string;
  failed_attempts?: number;
  status: Status;
  destinationCity?: City;
  originCity?: City;
  branch?: Branch;
  pricing?: Pricing;
  trackings?: Tracking[];
  scans?: Scan[];
  // Package information
  pieces?: number;
  height?: number;
  width?: number;
  length?: number;
  weight?: number;
  volumetric_weight?: number;
  chargeable_weight?: number;
  declared_value?: number;
  // Additional fields
  orderId?: string;
  goods_description?: string;
  special_instructions?: string;
  price?: number;
  discount?: number;
  total_price?: number;
  reason?: string; // For Short Arrived, Refused, Re-attempt statuses
  // Node fields
  first_mile_node_id?: number;
  current_node_id?: number;
  last_mile_node_id?: number;
  first_mile_node?: Branch;
  current_node?: Branch;
  last_mile_node?: Branch;
}

export interface PaginatedResponse<T> {
  current_page: number;
  data: T[];
  per_page: number;
  total: number;
}

export async function getConsignments(filters: ConsignmentFilters) {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "")
        params.append(k, String(v));
    });

    const response = await apiClient.get(`/consignments?${params.toString()}`);
    const responseData = response.data;

    // Unwrap common { success, data } envelope if present
    const payload =
      responseData &&
      typeof responseData === "object" &&
      "success" in responseData &&
      "data" in responseData
        ? (responseData as any).data
        : responseData;

    // If the payload already matches expected pagination shape
    if (
      payload &&
      typeof payload === "object" &&
      "data" in payload &&
      "current_page" in payload
    ) {
      return payload as PaginatedResponse<Consignment>;
    }

    // If payload is an array of consignments
    if (Array.isArray(payload)) {
      return {
        data: payload,
        current_page: 1,
        per_page: filters.per_page || 10,
        total: payload.length,
      } as PaginatedResponse<Consignment>;
    }

    // Attempt to normalize other shapes
    const normalized = {
      data: payload?.data || payload?.items || [],
      current_page: payload?.current_page || payload?.page || 1,
      per_page: payload?.per_page || payload?.limit || filters.per_page || 10,
      total:
        payload?.total ?? payload?.data?.length ?? payload?.items?.length ?? 0,
    } as PaginatedResponse<Consignment>;
    return normalized;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        (error.response.data as any)?.message || "Failed to fetch consignments",
      );
    }
    throw new Error("An unknown error occurred");
  }
}

export async function getConsignment(id: number) {
  try {
    const response = await apiClient.get(`/consignments/${id}`);
    const responseData = response.data;
    const payload =
      responseData &&
      typeof responseData === "object" &&
      "success" in responseData &&
      "data" in responseData
        ? (responseData as any).data
        : responseData;
    return payload as Consignment;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        (error.response.data as any)?.message || "Failed to fetch consignment",
      );
    }
    throw new Error("An unknown error occurred");
  }
}

export async function exportConsignmentsCsv(filters: ConsignmentFilters) {
  try {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "")
        params.append(k, String(v));
    });

    const response = await apiClient.get(
      `/consignments/export/csv?${params.toString()}`,
      {
        responseType: "blob",
      },
    );

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `consignments-${new Date().toISOString().split("T")[0]}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        error.response.data.message || "Failed to export consignments",
      );
    }
    throw new Error("An unknown error occurred");
  }
}

export interface ConsignmentTrackingDetails {
  consignment?: any;
  parcel_details?: {
    pieces?: number;
    weight?: number;
    length?: number;
    width?: number;
    height?: number;
    volumetric_weight?: number;
    chargeable_weight?: number;
  };
  financial_details?: {
    price?: number;
    cod_amount?: number;
    declared_value?: number;
  };
  tracking_history?: Array<{
    status?: { id?: number; name?: string } | string;
    description?: string;
    updated_by?: string;
    timestamp?: string;
  }>;
  created_at?: string;
  updated_at?: string;
}

export async function getConsignmentTracking(id: number) {
  try {
    const response = await apiClient.get(`/consignments/${id}/tracking`);
    const responseData = response.data;
    const payload =
      responseData &&
      typeof responseData === "object" &&
      "success" in responseData &&
      "data" in responseData
        ? (responseData as any).data
        : responseData;

    // Backend may return an array of tracking entries; normalize it
    if (Array.isArray(payload)) {
      const history = payload.map((t: any) => ({
        status: typeof t.status === "string" ? { name: t.status } : t.status,
        description:
          t.description ||
          t.details ||
          t.remark ||
          t.remarks ||
          t.note ||
          t.notes ||
          t.message ||
          "",
        timestamp: t.created_at || t.updated_at,
      }));
      return { tracking_history: history } as ConsignmentTrackingDetails;
    }

    // If payload already contains tracking_history
    if (
      payload &&
      typeof payload === "object" &&
      "tracking_history" in payload
    ) {
      const normalized = {
        ...payload,
        tracking_history: (payload.tracking_history || []).map((t: any) => ({
          status: typeof t.status === "string" ? { name: t.status } : t.status,
          description:
            t.description ||
            t.details ||
            t.remark ||
            t.remarks ||
            t.note ||
            t.notes ||
            t.message ||
            "",
          timestamp: t.timestamp || t.created_at || t.updated_at,
        })),
      } as ConsignmentTrackingDetails;
      return normalized;
    }

    // Fallback: wrap unknown object as tracking_history if it's an array-like
    return { tracking_history: [] } as ConsignmentTrackingDetails;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        (error.response.data as any)?.message ||
          "Failed to fetch tracking details",
      );
    }
    throw new Error("An unknown error occurred");
  }
}

// Bulk actions
export async function generateBookingSlips(filters: ConsignmentFilters) {
  // Open a guest streaming URL directly so reloads work (no blob/object URL)
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") params.append(k, String(v));
  });
  params.append("bookingSlip", "1");
  const publicUrl = `${API_BASE_URL}/public/consignments/bulk-action?${params.toString()}`;
  window.open(publicUrl, "_blank", "noopener,noreferrer");
}

export async function generatePrintList(filters: ConsignmentFilters) {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") params.append(k, String(v));
  });
  params.append("printList", "1");
  const publicUrl = `${API_BASE_URL}/public/consignments/bulk-action?${params.toString()}`;
  window.open(publicUrl, "_blank", "noopener,noreferrer");
}

// Individual actions
export async function printConsignment(id: number) {
  const publicUrl = `${API_BASE_URL}/public/consignments/${id}/print`;
  window.open(publicUrl, "_blank", "noopener,noreferrer");
}

export async function cancelConsignment(id: number, reason: string) {
  try {
    const response = await apiClient.post(`/consignments/${id}/cancel`, {
      reason: reason,
    });
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        error.response.data.message || "Failed to cancel consignment",
      );
    }
    throw new Error("An unknown error occurred");
  }
}

export interface ImportConsignmentsResponse {
  success: boolean;
  message: string;
  data?: {
    uploaded: number;
    failed: number;
    total: number;
    errors: Array<{
      ServiceType?: string;
      Destination?: string;
      OrderID?: string;
      CODAmount?: string;
      ConsigneePhone?: string;
      ConsigneeName?: string;
      ConsigneeSecondPhone?: string;
      ConsigneeAddress?: string;
      Pieces?: string;
      "Height(CM)"?: string;
      "Width(CM)"?: string;
      "Length(CM)"?: string;
      "Weight(KG)"?: string;
      DeclaredValue?: string;
      OrderType?: string;
      SpecialInstruction?: string;
      ERRORS: string;
    }>;
  };
}

export async function importConsignments(
  file: File,
): Promise<ImportConsignmentsResponse> {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await apiClient.post("/consignments/import", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        error.response.data.message || "Failed to import consignments",
      );
    }
    throw new Error("An unknown error occurred");
  }
}

export async function downloadSampleCsv() {
  const API_BASE_URL =
    process.env.REACT_APP_BACKEND_URL || "https://api.citycourier.org/api/v1";
  window.open(`${API_BASE_URL}/consignments/sample-csv`, "_blank");
}

// Create Consignment Types
export interface CreateConsignmentData {
  origin: number;
  destination: number;
  service_type: "Overnight" | "Express";
  pieces: number;
  height: number;
  width: number;
  length: number;
  weight: number;
  consignerName: string;
  consignerPhone: string;
  consignerPhone2?: string;
  consignerAddress: string;
  consignerCnic?: string;
  consigneeName: string;
  consigneePhone: string;
  consigneePhone2?: string;
  consigneeAddress: string;
  // CLIENT-specific fields
  cod_type?: "COD" | "Prepaid";
  orderId?: string;
  cod_amount?: number;
  declared_value?: number;
  special_instructions?: string;
  // Non-CLIENT fields
  goods_description?: string;
  price?: number;
  discount?: number;
  total_price?: number;
}

export interface CreateConsignmentResponse {
  success: boolean;
  data: Consignment;
  message: string;
}

/**
 * Create a new consignment
 */
export async function createConsignment(
  data: CreateConsignmentData,
): Promise<CreateConsignmentResponse> {
  try {
    const response = await apiClient.post("/consignments", data);
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        error.response.data.message || "Failed to create consignment",
      );
    }
    throw new Error("An unknown error occurred");
  }
}

// Scan Consignment Types
export interface ScanConsignmentRequest {
  consignment_no: string;
}

export interface ScanConsignmentData {
  id: number;
  consignment_no: string;
  booking_type: string;
  service_type: string;
  created_at: string;
  weight: number;
  cod_amount: number | null;
  status: string;
}

export interface ScanConsignmentResponse {
  success: boolean;
  data: ScanConsignmentData;
  message: string;
  id: number;
}

/**
 * Scan general consignment
 * Tries to mark as received at hub first, then at destination
 */
export async function scanGeneralConsignment(
  data: ScanConsignmentRequest,
): Promise<ScanConsignmentResponse> {
  try {
    const response = await apiClient.post("/consignments/scan", data);
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        error.response.data.message || "Failed to scan consignment",
      );
    }
    throw new Error("An unknown error occurred");
  }
}

/**
 * Scan pickup sheet consignment
 * Marks consignment as received at hub
 */
export async function scanPickupSheetConsignment(
  data: ScanConsignmentRequest,
): Promise<ScanConsignmentResponse> {
  try {
    const response = await apiClient.post(
      "/pickup-sheets/scan-consignment",
      data,
    );
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        error.response.data.message || "Failed to scan consignment",
      );
    }
    throw new Error("An unknown error occurred");
  }
}

/**
 * Scan master unit consignment
 * Marks consignment as received at destination
 */
export async function scanMasterUnitConsignment(
  data: ScanConsignmentRequest,
): Promise<ScanConsignmentResponse> {
  try {
    const response = await apiClient.post(
      "/master-units/scan-consignment",
      data,
    );
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        error.response.data.message || "Failed to scan consignment",
      );
    }
    throw new Error("An unknown error occurred");
  }
}

/**
 * Scan consignment for delivery sheet allocation
 * Validates consignment eligibility based on:
 * - Branch and destination matching user's branch
 * - Status: Booked (1), Arrived at hub (4), or Re-attempt to Deliver (11)
 * - Or consignment in master unit received at user's branch with status 7 or 11
 * - Excludes refused consignments (status 10) and those with 3+ failed attempts
 */
export async function scanDeliverySheetConsignment(
  data: ScanConsignmentRequest,
): Promise<ScanConsignmentResponse> {
  try {
    const response = await apiClient.post(
      "/delivery-sheets/scan-consignment",
      data,
    );
    return response.data;
  } catch (error: any) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(
        error.response.data.message || "Failed to scan consignment",
      );
    }
    throw new Error("An unknown error occurred");
  }
}
